import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useConfig, useGroups, useVehicleCatalog, useWorkshopServiceAdvisors } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { useTenantTerms } from "../lib/TenantConfigContext";
import { DOCUMENT_ACCEPT, SERVICE_TYPES, PICKUP_DROP_OPTIONS } from "../lib/journey";
import { PickupDropChoice } from "../components/journey/PickupDropChoice";
import { EMPTY_VEHICLE, vehicleChoiceError, vehiclePayload } from "../lib/vehicles";
import { joinUrls } from "../lib/bookingUploads";
import { useBookingUploads } from "../lib/useBookingUploads";
import { useBookingFlow } from "../lib/useBookingFlow";
import VehiclePicker from "../components/VehiclePicker";
import UploadSlot from "../components/booking/UploadSlot";
import BookingProgress from "../components/booking/BookingProgress";
import {
  BookingLayout,
  FORM_CARD,
  FormField,
  GroupPreview,
  NOTE,
  PLATE,
  PhoneInput,
  ROW2,
  ReviewList,
  STEP_BODY,
  StepSidebar,
  WizardFoot,
  bookingMembers,
  deliveryLabel,
  inputCls,
  vehiclePreview,
} from "../components/booking/WizardParts";
import { Btn, cx } from "../components/msd/ui";
import {
  DEFAULT_DELIVERY_TIME,
  isDeliveryPromiseInPast,
  isValidDeliveryTime,
  isValidDeliveryTimeline,
  toPromisedDeliveryISO,
} from "./CreateGroup";

const STEPS = [
  { key: "vehicle", label: "Vehicle Details", subtitle: "Number & model" },
  { key: "customer", label: "Customer Info", subtitle: "Name & phone number" },
  { key: "workshop", label: "Workshop & Delivery", subtitle: "Location, service & ETA" },
  { key: "documents", label: "Estimate & Job Card", subtitle: "Optional documents" },
  { key: "inventory", label: "Inventory", subtitle: "Optional photos & videos" },
  { key: "create", label: "Create Group", subtitle: "Review & create" },
];
const LAST = STEPS.length - 1;

const SLOTS = {
  estimate: { stage: "estimate" },
  jobCard: { stage: "job_card" },
  media: { stage: "media", multiple: true },
};
const DOCUMENT_KINDS = "PDF, Word, Excel, CSV, text or HTML files";
const MEDIA_ACCEPT = "image/*,video/*";

function deliveryBounds() {
  const today = new Date();
  const max = new Date(today);
  max.setFullYear(today.getFullYear() + 5);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { min: iso(today), max: iso(max) };
}

function timeSlots(dateStr) {
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const slots = [];
  for (let m = 0; m < 24 * 60; m += 30) {
    if (dateStr === todayStr && m <= nowMinutes) continue; // skip past slots today
    slots.push(`${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`);
  }
  return slots;
}

function slotLabel(v) {
  const [h, m] = v.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}

export default function MarutiBookService() {
  const { me } = useAuth();
  const { roleLabel } = useTenantTerms();
  const { data: cfg } = useConfig();
  const { data: advisorsData } = useWorkshopServiceAdvisors();
  // The same cached list the model picker reads; here it gives the preview its photo.
  const { data: catalog } = useVehicleCatalog();
  useGroups(); // warm the list the desk will open on
  const flow = useBookingFlow();

  const isServiceAdvisor = me?.role === "sa";
  const isSingleLocationLocked = me?.role && ["sa", "ccm", "wm", "psf", "receptionist"].includes(me.role);
  const isGm = me?.role === "gm";
  const userWorkshopLocation = me?.workshop_location;
  const userServiceAdvisorId = me?.service_advisor_id;
  const userName = me?.name;
  const userPhone = me?.phone;
  const gmLocations = useMemo(() => (Array.isArray(me?.workshop_locations) ? me.workshop_locations.filter(Boolean) : []), [me]);
  const allConfigLocations = (cfg?.rows || []).map((r) => r["Workshop Location"]).filter(Boolean);
  const locations = isGm ? gmLocations : isSingleLocationLocked && userWorkshopLocation ? [userWorkshopLocation] : allConfigLocations;

  const [step, setStep] = useState(0);
  const [vehicle, setVehicle] = useState(EMPTY_VEHICLE);
  const [f, setF] = useState({
    group_name: "",
    description: "",
    customer_name: "",
    customer_number: "",
    workshop_location: isSingleLocationLocked ? userWorkshopLocation || "" : "",
    service_advisor_id: isServiceAdvisor ? String(userServiceAdvisorId || "") : "",
    service_advisor_name: isServiceAdvisor ? userName || "" : "",
    service_advisor_number: isServiceAdvisor ? userPhone || "" : "",
    service_type: "",
    pickup_drop: "",
    delivery_date: "",
    delivery_time: "",
  });

  // Names only decide how an uploaded file is called, so the form-so-far is enough.
  const namesRef = useRef({});
  namesRef.current = { customerName: f.customer_name.trim(), vehicle: f.group_name.trim() };
  const getNames = useCallback(() => namesRef.current, []);
  const uploads = useBookingUploads({ slots: SLOTS, getNames });

  useEffect(() => {
    const updates = {};
    if (isSingleLocationLocked && userWorkshopLocation && !f.workshop_location) updates.workshop_location = userWorkshopLocation;
    if (isGm && gmLocations.length === 1 && !f.workshop_location) updates.workshop_location = gmLocations[0];
    if (isServiceAdvisor) {
      if (userServiceAdvisorId && !f.service_advisor_id) updates.service_advisor_id = String(userServiceAdvisorId);
      if (userName && !f.service_advisor_name) updates.service_advisor_name = userName;
      if (userPhone && !f.service_advisor_number) updates.service_advisor_number = userPhone;
    }
    if (Object.keys(updates).length) setF((s) => ({ ...s, ...updates }));
  }, [isSingleLocationLocked, isGm, isServiceAdvisor, userWorkshopLocation, gmLocations, userServiceAdvisorId, userName, userPhone, f.workshop_location, f.service_advisor_id, f.service_advisor_name, f.service_advisor_number]);

  // If the chosen time becomes past (e.g. user switches the date to today), clear it.
  useEffect(() => {
    if (f.delivery_date && f.delivery_time && isDeliveryPromiseInPast(f.delivery_date, f.delivery_time)) {
      setF((s) => ({ ...s, delivery_time: "" }));
    }
  }, [f.delivery_date, f.delivery_time]);

  const advisorsForLocation = (advisorsData?.rows || []).filter((a) => a.workshop_location === f.workshop_location);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target?.value ?? e }));
  const setPhoneDigits = (e) => setF((s) => ({ ...s, customer_number: (e.target.value || "").replace(/\D/g, "").slice(0, 10) }));
  const setLocation = (e) =>
    setF((s) => ({ ...s, workshop_location: e.target.value, service_advisor_id: "", service_advisor_name: "", service_advisor_number: "" }));
  const setAdvisor = (e) => {
    const id = e.target.value;
    const advisor = advisorsForLocation.find((a) => String(a.id) === id);
    setF((s) => ({ ...s, service_advisor_id: id, service_advisor_name: advisor?.name || "", service_advisor_number: advisor?.number || "" }));
  };

  const canNext = () => {
    if (step === 0) return !!f.group_name.trim() && !vehicleChoiceError(vehicle);
    if (step === 1) return !!f.customer_name.trim() && f.customer_number.length === 10;
    // The advisor is required: without one nobody is invited on their behalf
    // and the job card never appears in their desk.
    if (step === 2)
      return !!f.workshop_location && !!f.service_advisor_number && !!f.service_type && !!f.pickup_drop && isValidDeliveryTimeline(f.delivery_date) && isValidDeliveryTime(f.delivery_time) && !isDeliveryPromiseInPast(f.delivery_date, f.delivery_time);
    if (step === 3) return !uploads.busy && !uploads.failed("estimate") && !uploads.failed("jobCard");
    if (step === 4) return !uploads.busy && !uploads.failed("media");
    return true;
  };

  const next = () => {
    if (step === 2 && isDeliveryPromiseInPast(f.delivery_date, f.delivery_time)) {
      toast.error("That delivery time has already passed. Pick a later one.");
      return;
    }
    if (step < LAST) setStep(step + 1);
  };

  const submit = () => {
    const mediaUrls = uploads.urls("media");
    flow.start({
      payload: {
        group_name: f.group_name.trim(),
        description: f.description.trim(),
        customer_name: f.customer_name.trim(),
        customer_number: `+91${f.customer_number}`,
        workshop_location: f.workshop_location,
        service_advisor_name: f.service_advisor_name,
        service_advisor_number: f.service_advisor_number,
        service_type: f.service_type,
        pickup_drop: f.pickup_drop,
        delivery_timeline: f.delivery_date,
        promised_delivery_at: toPromisedDeliveryISO(f.delivery_date, f.delivery_time),
        ...vehiclePayload(vehicle),
        ...(uploads.urls("estimate")[0] ? { ro_estimate_url: uploads.urls("estimate")[0] } : {}),
        ...(uploads.urls("jobCard")[0] ? { ro_card_url: uploads.urls("jobCard")[0] } : {}),
        ...(mediaUrls.length ? { ro_media_urls: joinUrls(mediaUrls) } : {}),
      },
    });
  };

  const { min, max } = deliveryBounds();
  const nowDate = new Date();
  const todayStr = `${nowDate.getFullYear()}-${String(nowDate.getMonth() + 1).padStart(2, "0")}-${String(nowDate.getDate()).padStart(2, "0")}`;
  const isToday = f.delivery_date === todayStr;
  const pad = (n) => String(n).padStart(2, "0");
  const [selH = "", selM = ""] = (f.delivery_time || "").split(":");
  const hourOptions = Array.from({ length: 24 }, (_, h) => h).filter((h) => !isToday || h >= nowDate.getHours());
  const minuteOptions = Array.from({ length: 60 }, (_, m) => m).filter(
    (m) => !(isToday && selH !== "" && Number(selH) === nowDate.getHours() && m <= nowDate.getMinutes())
  );
  const setHour = (e) => {
    const h = e.target.value;
    if (h === "") {
      setF((s) => ({ ...s, delivery_time: "" }));
      return;
    }
    let m = Number(selM || 0);
    if (isToday && Number(h) === nowDate.getHours() && m <= nowDate.getMinutes()) m = Math.min(nowDate.getMinutes() + 1, 59);
    setF((s) => ({ ...s, delivery_time: `${pad(h)}:${pad(m)}` }));
  };
  const setMinute = (e) => setF((s) => ({ ...s, delivery_time: `${pad(selH)}:${pad(e.target.value)}` }));
  const deliveryPast = !!f.delivery_date && !!f.delivery_time && isDeliveryPromiseInPast(f.delivery_date, f.delivery_time);
  const creating = flow.active;
  const count = (slot) => uploads.urls(slot).length;
  const uploading = uploads.busy && (step === 3 || step === 4);
  const picture = vehiclePreview(vehicle, catalog?.rows);

  return (
    <BookingLayout
      sub="Creates the customer's WhatsApp service group"
      preview={
        <GroupPreview
          image={picture.image}
          name={f.group_name.trim()}
          sub={picture.sub}
          description={f.description.trim()}
          meta={[
            ["Workshop", f.workshop_location || "Not chosen"],
            ["Work", f.service_type || "Not chosen"],
            ["Delivery", deliveryLabel(f.delivery_date, f.delivery_time)],
          ]}
          members={bookingMembers({
            customerName: f.customer_name.trim(),
            customerPhone: f.customer_number.length === 10 ? `+91 ${f.customer_number}` : "",
            advisorName: f.service_advisor_name || f.service_advisor_number,
            advisorPhone: f.service_advisor_number ? `+91 ${f.service_advisor_number.replace(/\D/g, "").slice(-10)}` : "",
            advisorRole: roleLabel("sa"),
            hasWorkshop: !!f.workshop_location,
          })}
        />
      }
    >
      <section className={FORM_CARD}>
        <StepSidebar steps={STEPS} currentStep={step} completedUpTo={creating ? LAST : step} />

        <div className={STEP_BODY}>
          {step === 0 && (
            <>
              <FormField label="Vehicle number" required hint="Becomes the WhatsApp group name.">
                <input data-testid="input-group-name" autoFocus className={cx(inputCls, PLATE)} value={f.group_name} onChange={set("group_name")} placeholder="e.g., MH12AB1234" />
              </FormField>
              <FormField group label="Model" required>
                <VehiclePicker value={vehicle} onChange={setVehicle} inputClassName={inputCls} hint="Its photo becomes the group picture." />
              </FormField>
              <FormField label="Group description" optional>
                <input data-testid="input-description" className={inputCls} value={f.description} onChange={set("description")} />
              </FormField>
            </>
          )}

          {step === 1 && (
            <>
              <FormField label="Customer name" required>
                <input data-testid="input-customer-name" autoFocus className={inputCls} value={f.customer_name} onChange={set("customer_name")} placeholder="Full name" />
              </FormField>
              <FormField label="WhatsApp number" required hint="The customer gets the group invite on this number.">
                <PhoneInput data-testid="input-customer-number" placeholder="10-digit mobile number" inputMode="numeric" value={f.customer_number} onChange={setPhoneDigits} />
              </FormField>
            </>
          )}

          {step === 2 && (
            <>
              <FormField label="Workshop location" required>
                <select data-testid="select-location" className={inputCls} value={f.workshop_location} onChange={setLocation} disabled={isSingleLocationLocked}>
                  <option value="">Select location…</option>
                  {locations.map((l) => (
                    <option key={l} value={l}>{l}</option>
                  ))}
                </select>
              </FormField>
              <FormField label={roleLabel("sa")} required>
                {isServiceAdvisor ? (
                  <input data-testid="input-service-advisor" className={inputCls} value={f.service_advisor_name || f.service_advisor_number || "You"} disabled />
                ) : (
                  <select data-testid="select-service-advisor" className={inputCls} value={f.service_advisor_id} onChange={setAdvisor} disabled={!f.workshop_location}>
                    <option value="">{f.workshop_location ? "Select service advisor…" : "Select a workshop location first"}</option>
                    {advisorsForLocation.map((a) => (
                      <option key={a.id} value={a.id}>{a.name || a.number}{a.number ? ` (${a.number})` : ""}</option>
                    ))}
                  </select>
                )}
              </FormField>
              <FormField label="Service type" required>
                <select data-testid="select-service-type" className={inputCls} value={f.service_type} onChange={set("service_type")}>
                  <option value="">Select service type…</option>
                  {SERVICE_TYPES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </FormField>
              <FormField group label="Pick & drop" required>
                <PickupDropChoice value={f.pickup_drop} onChange={(v) => setF((s) => ({ ...s, pickup_drop: v }))} />
              </FormField>
              <FormField group label="Promised delivery" required hint="When you've committed the vehicle will be ready">
                <div className={ROW2}>
                  {deliveryPast && <p className="text-xs text-red-600 mt-1">That time has already passed. Pick a later time.</p>}
                  <input
                    type="date"
                    data-testid="input-delivery-timeline"
                    className={inputCls}
                    min={min}
                    max={max}
                    value={f.delivery_date}
                    onChange={(e) => {
                      const nextDate = e.target.value;
                      if (!nextDate || isValidDeliveryTimeline(nextDate)) {
                        setF((s) => {
                          const t = s.delivery_time || DEFAULT_DELIVERY_TIME;
                          return { ...s, delivery_date: nextDate, delivery_time: nextDate && !isDeliveryPromiseInPast(nextDate, t) ? t : "" };
                        });
                      }
                    }}
                  />
                  <div className="flex gap-2">
                    <select data-testid="input-delivery-time" aria-label="Delivery hour" className={inputCls} value={selH === "" ? "" : String(Number(selH))} onChange={setHour} disabled={!f.delivery_date}>
                      <option value="">{f.delivery_date ? "Hour" : "Pick date first"}</option>
                      {hourOptions.map((h) => (
                        <option key={h} value={h}>{`${h % 12 || 12} ${h >= 12 ? "PM" : "AM"}`}</option>
                      ))}
                    </select>
                    <select aria-label="Delivery minute" className={inputCls} value={selM === "" ? "" : String(Number(selM))} onChange={setMinute} disabled={selH === ""}>
                      <option value="">Min</option>
                      {minuteOptions.map((m) => (
                        <option key={m} value={m}>{pad(m)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </FormField>
            </>
          )}

          {step === 3 && (
            <>
              <p className={NOTE}>
                Optional. The estimate is sent when the customer taps Let&apos;s get started, and the job card once they approve it. Files upload as you add them.
              </p>
              <UploadSlot
                label="Estimate"
                kinds={DOCUMENT_KINDS}
                accept={DOCUMENT_ACCEPT}
                items={uploads.items("estimate")}
                onPick={(files) => uploads.pick("estimate", files)}
                onRemove={(id) => uploads.remove("estimate", id)}
                onRetry={(id) => uploads.retry("estimate", id)}
                testid="booking-doc-estimate"
              />
              <UploadSlot
                label="Job card"
                kinds={DOCUMENT_KINDS}
                accept={DOCUMENT_ACCEPT}
                items={uploads.items("jobCard")}
                onPick={(files) => uploads.pick("jobCard", files)}
                onRemove={(id) => uploads.remove("jobCard", id)}
                onRetry={(id) => uploads.retry("jobCard", id)}
                testid="booking-doc-jobCard"
              />
            </>
          )}

          {step === 4 && (
            <>
              <p className={NOTE}>
                Optional. Inventory photos and videos taken at drop-off are sent when the customer taps Let&apos;s get started. Each file uploads as you add it, so take your time.
              </p>
              <UploadSlot
                label="Inventory photos / videos"
                kinds="images or videos"
                accept={MEDIA_ACCEPT}
                multiple
                items={uploads.items("media")}
                onPick={(files) => uploads.pick("media", files)}
                onRemove={(id) => uploads.remove("media", id)}
                onRetry={(id) => uploads.retry("media", id)}
                testid="booking-doc-media"
              />
            </>
          )}

          {step === LAST && !creating && (
            <div className="flex flex-col gap-2" data-testid="booking-review">
              <p className={NOTE}>Check the details, then create the group. The customer and your team are invited once it&apos;s ready.</p>
              <ReviewList
                rows={[
                  ["Vehicle", f.group_name.trim()],
                  ["Model", vehicle.notListed ? vehicle.typed.trim() : "Selected from the list"],
                  ["Customer", `${f.customer_name.trim()} · +91${f.customer_number}`],
                  ["Workshop", f.workshop_location],
                  [roleLabel("sa"), f.service_advisor_name || f.service_advisor_number],
                  ["Service", f.service_type],
                  ["Pick & drop", (PICKUP_DROP_OPTIONS.find(([k]) => k === f.pickup_drop) || [])[1]],
                  ["Promised", `${f.delivery_date} ${f.delivery_time}`],
                  ["Files", `${count("estimate") + count("jobCard")} document(s), ${count("media")} photo/video(s)`],
                ]}
              />
            </div>
          )}

          {step === LAST && creating && <BookingProgress flow={flow} />}
        </div>

        {!creating && (
          <WizardFoot>
            {step > 0 ? <Btn onClick={() => setStep(step - 1)}>Back</Btn> : <span />}
            <Btn
              variant="pri"
              icon={step === LAST ? "wa" : undefined}
              onClick={step === LAST ? submit : next}
              disabled={!canNext()}
              data-testid={step === LAST ? "submit-create-group" : "booking-next"}
            >
              {uploading ? (
                <>
                  <Loader2 className="animate-spin" size={15} /> Uploading…
                </>
              ) : step === LAST ? (
                "Create group"
              ) : step >= 3 && count(step === 3 ? "estimate" : "media") + (step === 3 ? count("jobCard") : 0) === 0 ? (
                "Skip"
              ) : (
                "Continue"
              )}
            </Btn>
          </WizardFoot>
        )}
      </section>
    </BookingLayout>
  );
}
